import { moneyFormat } from "@redotech/money/util";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { ShipmentTrackerStatus } from "@redotech/redo-fulfillment-group-common/outbound-shipment-label";
import { Parcel } from "@redotech/redo-fulfillment-parcel-common/parcel";
import { LRUCache } from "@redotech/util/cache";

export function formatParcelName(input: string): string {
  return (
    input
      // insert a space between letter and number
      .replace(/([A-z])([0-9])/g, "$1 $2")

      // insert a space between lower & upper
      .replace(/([a-z])([A-Z])/g, "$1 $2")

      // space before last upper in a sequence followed by lower
      .replace(/\b([A-Z]+)([A-Z])([a-z])/, "$1 $2$3")

      // uppercase the first character
      .replace(/^./, function (str) {
        return str.toUpperCase();
      })

      // format FedEx correctly
      .replace("Fed Ex", "FedEx")
  );
}

export function formatDateString(
  isoDate: string | undefined,
  ignoreLocale = false,
): string {
  if (!isoDate) {
    return "";
  }
  try {
    return new Date(isoDate).toLocaleString("default", {
      month: "short",
      day: "numeric",
      year: "numeric",
      ...(ignoreLocale && { timeZone: "UTC" }),
    });
  } catch (e) {
    console.error(e);
    return "";
  }
}

export function getCustomerName(row: FulfillmentGroupData): string {
  return row.customerName ?? "";
}

export function getCustomerShippingMethod(
  row: FulfillmentGroupData | undefined,
): string {
  return row?.shippingMethodNames[0] ?? "";
}

export function getCustomerShippingPrice(row: FulfillmentGroupData): string {
  return moneyFormat({
    amount: +row.totalShippingCost.amount,
    currency: row.totalShippingCost.currency,
  });
}

const expandedItemsCache = new LRUCache<
  FulfillmentGroupData,
  FulfillmentGroupData["items"]
>(200);

export function getExpandedItems(
  group: FulfillmentGroupData,
): FulfillmentGroupData["items"] {
  const cached = expandedItemsCache.get(group);
  if (cached) {
    return cached;
  }

  if (!group.items || !Array.isArray(group.items)) {
    return [];
  }

  const expanded = group.items.flatMap((item) =>
    Array.from({ length: item.quantity }, () => ({
      ...item,
      quantity: 1,
      totalQuantity: 1,
    })),
  );

  expandedItemsCache.set(group, expanded);
  return expanded;
}

export function getOrderAge(row: FulfillmentGroupData): string {
  const orderDate = getOrderDate(row);

  if (!orderDate) {
    return "";
  }

  const now = Temporal.Now.instant();
  const orderInstant = Temporal.Instant.from(orderDate.toISOString());
  const diff = now.since(orderInstant);

  const totalMinutes = diff.total({ unit: "minutes" });
  const totalHours = diff.total({ unit: "hours" });
  const totalDays = diff.total({ unit: "days" });

  if (totalMinutes < 60) {
    const minutes = Math.floor(totalMinutes);
    return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  }

  if (totalHours < 48) {
    const hours = Math.floor(totalHours);
    return `${hours} hour${hours === 1 ? "" : "s"}`;
  }

  const days = Math.floor(totalDays);
  return `${days} day${days === 1 ? "" : "s"}`;
}

export function getOrderDate(
  row: FulfillmentGroupData,
): Date | undefined | null {
  return row?.orderCreatedAt;
}

export function getOrderName(row: FulfillmentGroupData): string {
  if (!row.orders || !Array.isArray(row.orders)) {
    return "";
  }
  return row.orders.map((order) => order.name).join(", ");
}

export function getParcelName(parcel: Parcel): string {
  return "carrier" in parcel ? formatParcelName(parcel.name) : parcel.name;
}

export function getRecipientName(row: FulfillmentGroupData): string {
  return row.destinationAddress?.name ?? "";
}

export function getShipmentCreatedAt(row: FulfillmentGroupData): Date {
  return row.providerCreatedAt;
}

export function formatOrderSubtotal(row: FulfillmentGroupData): string {
  return moneyFormat({
    amount: Number(row.totalDiscountedPrice?.amount ?? row.totalPrice.amount),
    currency: row.totalPrice.currency,
  });
}

export function formatTaxTotal(row: FulfillmentGroupData): string {
  return moneyFormat({
    amount: Number(row.totalTax.amount),
    currency: row.totalTax.currency,
  });
}

export function formatDeliveryDateCell(row: FulfillmentGroupData): string {
  const tracker = row.shipment?.tracker;
  if (tracker?.status === ShipmentTrackerStatus.DELIVERED) {
    const deliveredDetail = [...(tracker.trackingDetails ?? [])]
      .reverse()
      .find((detail) => detail.status === ShipmentTrackerStatus.DELIVERED);
    if (!deliveredDetail) {
      return "";
    }
    return formatDateString(deliveredDetail.datetime.toISOString());
  }
  const estDeliveryDate = tracker?.estDeliveryDate?.toISOString();
  if (!estDeliveryDate) {
    return "";
  }
  return `Est. ${formatDateString(estDeliveryDate)}`;
}
